import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { PageRoutes } from "../../../constants/routes";
import { appActions } from "../../../ducks/app";
import { authSelectors } from "../../../ducks/auth";
import { Group, groupActions } from "../../../ducks/group";
import { UserRole } from "../../../ducks/user";
import { useNavigation } from "../../../hooks/useNavigation";
import { AppDispatch } from "../../../store";
import { formatDateTime } from "../../../utils/date";
import { DetailItem } from "../../templates/DetailTemplate";

export const useGroupDetail = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { navigateToPage } = useNavigation();
  const { groupId } = useParams<{ groupId: string }>();

  const loggedInUser = useSelector(authSelectors.loggedInUser);

  const [group, setGroup] = useState<Group | null>(null);

  const canEdit = useMemo(() => {
    if (!loggedInUser) return false;

    return loggedInUser.role === UserRole.Admin;
  }, [loggedInUser]);

  useEffect(() => {
    if (group || !groupId) return;

    dispatch(appActions.showLoading());

    dispatch(
      groupActions.getGroupDetails.started({
        groupId,
        onComplete: () => dispatch(appActions.hideLoading()),
        onSuccess: (data) => {
          const groupData = data.data;
          const group: Group = {
            id: groupData.id,
            name: groupData.name,
            clientName: groupData.customer_name,
            createdAt: new Date(groupData.created_at),
            createdBy: groupData.created_by,
          };

          setGroup(group);
        },
        onError: () => {
          navigateToPage(PageRoutes.GROUPS);
        },
      }),
    );
  }, []);

  const groupDetail: DetailItem[] = useMemo(() => {
    if (!group) return [];

    return [
      {
        name: "顧客名",
        value: group.clientName,
      },
      {
        name: "グループ名",
        value: group.name,
      },
      {
        name: "登録日",
        value: group.createdAt
          ? formatDateTime(group.createdAt, false, "YYYY/MM/DD HH:mm")
          : "",
      },
      {
        name: "登録者",
        value: group.createdBy ?? "",
      },
    ];
  }, [group]);

  const handleCancel = useCallback(() => {
    navigateToPage(PageRoutes.GROUPS);
  }, []);

  const handleEdit = useCallback(() => {
    if (!groupId) return;

    navigateToPage(PageRoutes.GROUP_EDIT.replace(":groupId", groupId));
  }, [groupId]);

  return { canEdit, groupDetail, handleCancel, handleEdit };
};
